// packages/core/src/learning/core-modules/debt-credit-interest.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 04: "Debt, Credit & Interest" (tier: control)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Impact Lab link: lesson 3 deep-links to existing scenario
// 'minimum-payments-trap' (verified present in ImpactLab_impactScenarios.ts).
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'debt-credit-interest--debt-borrows-from-future-you',
	title: 'Debt borrows from future you',
	description:
		'You learn to see debt as a claim on future cash flow, not a personal failure.',
	pillar: 'debt-credit-interest',
	schoolsOfThought: [
		'personal-finance-basics',
		'debt-free-ramsey',
		'behavioral-finance',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['debt', 'cash-flow', 'future-self', 'payments'],
	prerequisites: [],
	relatedLessons: ['debt-credit-interest--interest-is-the-price-of-time'],
	concepts: [
		'debt-as-future-cash-flow',
		'payment-pressure',
		'borrowed-options',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Debt is money you already used, but your future self still has to pay for. That does not make you bad. It means part of your future cash flow has already been claimed. A credit card balance, car loan, student loan, personal loan, buy-now-pay-later plan, or family debt all pull money from future months.',
		},
		{
			type: 'paragraph',
			text: 'This matters because debt is not only a balance. It is pressure. A $3,000 debt does not just sit there as a number. It may create minimum payments, interest, stress, late fees, and fewer options every month. The real question is not “Am I a bad person for having debt?” The better question is “How much future room is this taking?”',
		},
		{
			type: 'bullet_list',
			items: [
				'Debt reduces future cash flow',
				'Debt can create stress',
				'Debt can limit choices',
				'Debt can grow through interest',
				'Debt can be managed with clarity',
			],
		},
		{
			type: 'callout',
			icon: '🧱',
			text: 'Debt is not your identity. It is a claim on future money.',
		},
		{
			type: 'paragraph',
			text: 'People get into debt for different reasons. Emergencies. Low income. School. Transportation. Helping family. Medical costs. Bad habits. Bad timing. Bad decisions. Sometimes debt was the only bridge available. Sometimes it came from avoidance or impulse. Both can be true without turning the lesson into shame.',
		},
		{
			type: 'example',
			label: 'Monthly pressure',
			text: 'Suppose you owe $4,000 across cards and loans. The balance matters, but the monthly pressure matters too. If the minimum payments total $220, then $220 of every month is already spoken for before you save, invest, move, travel, or build runway.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is only thinking about debt as a total amount. Total debt tells you the size of the hole. Monthly debt payments tell you how much oxygen the hole is taking right now. Someone with $10,000 in debt and low required payments may feel different from someone with $3,000 due aggressively with high interest and tight income.',
		},
		{
			type: 'paragraph',
			text: 'Think of debt like a subscription to a past decision. Some subscriptions were necessary. Some were not. Either way, they keep billing future you until they are handled. The goal is not to hate yourself for the past. The goal is to stop letting old decisions quietly run the whole month.',
		},
		{
			type: 'bullet_list',
			items: [
				'Name the balance',
				'Name the payment',
				'Name the interest',
				'Name the due date',
				'Name what it costs your monthly gap',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Debt becomes less scary when you stop treating it as shame and start treating it as future cash flow already claimed.',
		},
	],
	audioScript:
		'Debt is one of those topics that can instantly make people feel judged. So let’s get the frame right. Debt is not your identity. It is not proof that you are stupid, lazy, or broken. Debt is money you used before your future self fully paid for it. That is it. The real issue is what that debt does to your future cash flow. A credit card balance, car loan, student loan, personal loan, buy-now-pay-later plan, or family debt all have one thing in common: they claim money from future months. And that claim reduces your options. If you owe four thousand dollars and your minimum payments total two hundred twenty dollars a month, that two hundred twenty is already spoken for. It cannot also become runway, investing, moving money, travel money, or breathing room. That is why debt is not only a balance. It is pressure. Now, people get into debt for all kinds of reasons. Emergencies. Low income. School. Cars. Family. Medical stuff. Stress. Avoidance. Impulse. Sometimes debt was a bridge. Sometimes it was a mistake. Usually, life is messier than one label. The goal here is not shame. The goal is clarity. The common mistake is only looking at the total amount. Total debt tells you the size of the hole. Monthly payments tell you how much oxygen the hole is taking right now. Think of debt like a subscription to a past decision. Some were necessary. Some were not. But they keep billing future you until you deal with them. The first move is boring but powerful: name the balance, payment, interest, due date, and monthly pressure. Once you can see it clearly, debt stops being a fog and becomes a problem you can start working with.',
	flashcards: [
		{
			id: 'debt-credit-interest--debt-borrows-from-future-you--fc-main',
			role: 'main_idea',
			front: 'What is debt?',
			back: 'Debt is money already used that future cash flow still has to repay.',
		},
		{
			id: 'debt-credit-interest--debt-borrows-from-future-you--fc-watch',
			role: 'watch_out',
			front: 'Why is debt not just a balance?',
			back: 'Because debt also creates monthly payment pressure, interest, stress, and fewer options.',
		},
		{
			id: 'debt-credit-interest--debt-borrows-from-future-you--fc-use',
			role: 'use_it',
			front: 'What should you list for each debt?',
			back: 'List the balance, payment, interest rate, due date, and what it costs your monthly gap.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Map your debt pressure',
		prompt: 'Write each debt you owe, then list its balance, minimum payment, interest rate if known, due date, and monthly pressure.',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'debt-credit-interest--interest-is-the-price-of-time',
	title: 'Interest is the price of time',
	description:
		'You learn how interest changes debt from a one-time purchase into a cost that can keep growing.',
	pillar: 'debt-credit-interest',
	schoolsOfThought: [
		'personal-finance-basics',
		'credit-optimization',
		'fi-math',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['interest', 'apr', 'credit-card', 'debt-cost'],
	prerequisites: ['debt-credit-interest--debt-borrows-from-future-you'],
	relatedLessons: ['debt-credit-interest--minimum-payments-protect-lenders'],
	concepts: [
		'interest-as-price-of-time',
		'apr-basics',
		'compounding-against-you',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Interest is the price of using someone else’s money over time. If you borrow money and pay it back later, the lender usually charges for that time. That charge may look small monthly, but it can become heavy when the balance sticks around.',
		},
		{
			type: 'paragraph',
			text: 'This matters because interest changes the real cost of debt. A $500 purchase is not always just $500 if it sits on a credit card. A $12,000 car is not just $12,000 if the loan adds thousands over the repayment period. The price tag is the first cost. Interest is the time cost.',
		},
		{
			type: 'bullet_list',
			items: [
				'Principal is the amount borrowed',
				'Interest is the cost of borrowing',
				'APR is the yearly rate',
				'Minimum payments may move slowly',
				'High rates can grow pressure fast',
			],
		},
		{
			type: 'callout',
			icon: '⏳',
			text: 'Interest means time has a price. The longer debt stays around, the more time can cost.',
		},
		{
			type: 'paragraph',
			text: 'Interest can work for you or against you. When savings or investments grow, time may help you. When high-interest debt grows, time may work against you. This is why debt can feel so frustrating. You make payments, but the balance barely moves because part of the payment goes to interest instead of reducing what you owe.',
		},
		{
			type: 'example',
			label: 'Simple example',
			text: 'Imagine a credit card balance of $1,000 with a high interest rate. If the payment is small, part of that payment covers interest first. That means only the rest reduces the balance. The purchase is already done, but the time cost keeps showing up.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is looking only at the monthly payment. A low payment can feel manageable, but it may hide a long repayment timeline. This is how a debt can feel “under control” while quietly staying alive for months or years. The payment is not the whole story. The rate and timeline matter too.',
		},
		{
			type: 'paragraph',
			text: 'Think of interest like rent charged on borrowed money. If you keep using the borrowed space, you keep paying rent. The faster the balance shrinks, the less borrowed space remains. The slower it shrinks, the longer rent keeps showing up.',
		},
		{
			type: 'bullet_list',
			items: [
				'Know the balance',
				'Know the rate',
				'Know the payment',
				'Notice how much goes to interest',
				'Watch the timeline',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Interest is the hidden time cost that can make debt much more expensive than the original purchase.',
		},
	],
	audioScript:
		'Interest is the price of time. That is the cleanest way to think about it. When you borrow money, you are using someone else’s money now and paying it back later. The lender charges for that gap. That charge is interest. And the longer the debt sticks around, the more time can cost. This is why a purchase is not always just the price on the receipt. A five hundred dollar charge can cost more than five hundred if it sits on a credit card. A car loan can cost more than the sticker price because the loan adds interest over time. Principal is the amount you borrowed. Interest is the borrowing cost. APR is the yearly rate. The confusing part is that the monthly payment can make debt feel smaller than it really is. A low payment may feel manageable, but it can hide the timeline. You may be paying every month and still watching the balance barely move because part of the payment goes to interest first. That is what makes high-interest debt feel so annoying. You are not only paying for the thing. You are paying for the time the debt stayed alive. Interest can also work in the other direction. When savings or investments grow, time may help you. But when expensive debt grows, time may work against you. Same force, different side of the table. Think of interest like rent charged on borrowed money. If you keep using the borrowed space, the rent keeps showing up. The goal in this lesson is not to panic. It is to see the real cost. Balance, rate, payment, interest, timeline. Those five pieces tell a much clearer story than the minimum payment alone.',
	flashcards: [
		{
			id: 'debt-credit-interest--interest-is-the-price-of-time--fc-main',
			role: 'main_idea',
			front: 'What is interest?',
			back: 'Interest is the cost of borrowing money over time.',
		},
		{
			id: 'debt-credit-interest--interest-is-the-price-of-time--fc-watch',
			role: 'watch_out',
			front: 'Why can monthly payments be misleading?',
			back: 'A low payment can hide a long timeline and a lot of interest.',
		},
		{
			id: 'debt-credit-interest--interest-is-the-price-of-time--fc-use',
			role: 'use_it',
			front: 'What debt details show interest pressure?',
			back: 'Balance, interest rate, payment amount, and repayment timeline show the real pressure.',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a $50 monthly interest drag',
		amount: 50,
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'debt-credit-interest--minimum-payments-protect-lenders',
	title: 'Minimum payments protect lenders',
	description:
		'You learn why minimum payments can keep debt alive even when you feel like you are doing the responsible thing.',
	pillar: 'debt-credit-interest',
	schoolsOfThought: [
		'debt-free-ramsey',
		'credit-optimization',
		'behavioral-finance',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['minimum-payments', 'credit-card-debt', 'interest', 'debt-trap'],
	prerequisites: ['debt-credit-interest--interest-is-the-price-of-time'],
	relatedLessons: ['debt-credit-interest--credit-is-a-tool-not-free-money'],
	concepts: ['minimum-payment-trap', 'debt-timeline', 'payment-vs-progress'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Minimum payments are the smallest required payment that keeps an account current. Paying the minimum is better than missing a payment, but it is not the same as making strong progress. Minimums are often designed to keep the debt alive while collecting interest over time.',
		},
		{
			type: 'paragraph',
			text: 'This matters because minimum payments can create a false feeling of control. You see “payment made” and feel responsible. That may be true in one sense: you avoided a late fee and protected the account from becoming delinquent. But the balance may still be shrinking slowly, especially if the interest rate is high.',
		},
		{
			type: 'bullet_list',
			items: [
				'Minimums avoid immediate trouble',
				'Minimums may reduce the balance slowly',
				'Minimums can stretch the timeline',
				'Minimums can increase total interest paid',
			],
		},
		{
			type: 'callout',
			icon: '🪤',
			text: 'A minimum payment can keep you current without really getting you free.',
		},
		{
			type: 'paragraph',
			text: 'The key difference is payment versus progress. A payment is money leaving your account. Progress is how much closer you are to being done. Those are not always the same thing. If most of a payment goes to interest, the debt may survive longer than expected.',
		},
		{
			type: 'example',
			label: 'Realistic trap',
			text: 'Suppose someone has a $2,400 credit card balance and only pays a small minimum each month. They may feel like they are handling it because they never miss a payment. But if the balance is high-interest and the payment stays low, the payoff timeline can stretch much longer than the original purchase deserved.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is letting the lender set the pace by default. The minimum is not a personal payoff strategy. It is the floor required to avoid immediate consequences. A real strategy asks a different question: “What payment would actually move this debt in a meaningful timeline?”',
		},
		{
			type: 'paragraph',
			text: 'Think of minimum payments like walking on a treadmill. You are moving, but you may not be getting far. To actually leave the treadmill, the payment has to do more than keep the machine running. It has to reduce the balance enough to change the timeline.',
		},
		{
			type: 'bullet_list',
			items: [
				'Paying current is one goal',
				'Reducing balance is another',
				'Lowering interest cost is another',
				'Getting options back is the bigger goal',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Minimum payments can prevent immediate damage, but they may not be enough to buy back your future options.',
		},
	],
	audioScript:
		'Minimum payments are tricky because they feel responsible. And sometimes they are. If you pay the minimum, you avoid missing the payment. You may avoid a late fee. You may keep the account current. That is better than ignoring the bill. But here is the trap: minimum payments are not built to get you free quickly. They are the smallest payment required to keep the account alive. And if the interest rate is high, that can mean the debt stays around much longer than you expect. This is the difference between payment and progress. A payment is money leaving your account. Progress is how much closer you are to being done. Those are not always equal. If a big chunk of your payment goes to interest, the balance may barely move. So you can feel like you are doing the right thing while the timeline stays ugly. Imagine someone has a twenty-four hundred dollar credit card balance and pays only a small minimum each month. They never miss a payment, so emotionally it feels handled. But if the balance is high-interest and the payment stays low, that old spending can keep billing future months for a long time. The common mistake is letting the lender set the pace. The minimum payment is not your plan. It is the floor. A real plan asks, “What payment would actually change the timeline?” Think of minimum payments like walking on a treadmill. You are moving. You are sweating. Money is leaving. But are you getting anywhere? That is the question. Minimums can keep you current. They may not get you free.',
	flashcards: [
		{
			id: 'debt-credit-interest--minimum-payments-protect-lenders--fc-main',
			role: 'main_idea',
			front: 'What is a minimum payment?',
			back: 'It is the smallest required payment that keeps the account current.',
		},
		{
			id: 'debt-credit-interest--minimum-payments-protect-lenders--fc-watch',
			role: 'watch_out',
			front: 'Why can minimums be a trap?',
			back: 'They can keep debt alive for a long time while interest keeps adding cost.',
		},
		{
			id: 'debt-credit-interest--minimum-payments-protect-lenders--fc-use',
			role: 'use_it',
			front: 'What question beats minimum-payment thinking?',
			back: 'Ask, “What payment would actually move this debt in a meaningful timeline?”',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'See the minimum-payment trap',
		scenarioId: 'minimum-payments-trap',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'debt-credit-interest--credit-is-a-tool-not-free-money',
	title: 'Credit is a tool, not free money',
	description:
		'You learn how credit can help or hurt depending on behavior, timing, interest, and repayment.',
	pillar: 'debt-credit-interest',
	schoolsOfThought: [
		'credit-optimization',
		'adulting-life-skills',
		'risk-management',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['credit', 'credit-card', 'credit-score', 'borrowing'],
	prerequisites: ['debt-credit-interest--minimum-payments-protect-lenders'],
	relatedLessons: ['debt-credit-interest--pick-a-payoff-lane'],
	concepts: ['credit-as-tool', 'utilization-behavior', 'repayment-trust'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Credit lets you use money now and repay later. That can be useful. It can help with payment history, renting, emergencies, travel protections, fraud protection, or timing when used carefully. But credit is not extra income. It is access to borrowed money, and borrowed money has rules.',
		},
		{
			type: 'paragraph',
			text: 'This matters because credit can make life easier or more expensive depending on behavior. The same card can be a convenience tool for one person and a debt trap for another. The difference is not the plastic. The difference is repayment, interest, limits, habits, and whether the user treats credit like a tool or like income.',
		},
		{
			type: 'bullet_list',
			items: [
				'Credit can help build history',
				'Credit can smooth timing',
				'Credit can create debt',
				'Credit can charge high interest',
				'Credit can reward discipline but punish drift',
			],
		},
		{
			type: 'callout',
			icon: '💳',
			text: 'Credit is useful when it supports your plan. It is dangerous when it replaces your plan.',
		},
		{
			type: 'paragraph',
			text: 'A credit score is not a measure of your worth. It is a signal lenders use to guess how risky it is to lend to you. Payment history, balances, account age, credit mix, and inquiries can matter. But the score is not the deeper goal. The deeper goal is a money life where borrowed money does not quietly control you.',
		},
		{
			type: 'example',
			label: 'Same card, different outcome',
			text: 'Person A uses a credit card for planned groceries and pays it off before interest becomes a problem. Person B uses the same type of card for unplanned spending, carries the balance, and pays interest for months. The tool is similar. The cash flow result is completely different.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is thinking rewards make spending smarter by default. Points, cash back, and perks can feel like winning, but they do not cancel out interest, overspending, or a balance that sticks around. A 2% reward does not beat a high-interest balance that follows you for months.',
		},
		{
			type: 'paragraph',
			text: 'Think of credit like a sharp kitchen knife. Useful in the right hands, dangerous when careless, and not evil by itself. The question is not “Is credit good or bad?” The better question is “Does this tool help me keep control, or does it make my cash flow easier to ignore?”',
		},
		{
			type: 'bullet_list',
			items: [
				'Know what you charged',
				'Know when it is due',
				'Know whether interest will hit',
				'Know if it fits your cash flow',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Credit is not free money; it is a tool that either supports your cash flow or quietly attacks it.',
		},
	],
	audioScript:
		'Credit is a tool, not free money. That sounds basic, but a lot of people get hurt because credit feels like extra room when it is really borrowed room. A credit card can be useful. It can help build payment history. It can protect purchases. It can make timing easier. It can be convenient. But the same card can also become high-interest debt if spending drifts and the balance sticks around. The tool is not the whole story. The behavior is the story. One person uses a card for planned groceries and pays it off before interest becomes a problem. Another person uses the same kind of card for random spending, carries the balance, and pays interest for months. Same tool, different outcome. And credit scores can make this feel more confusing. A credit score is not your worth as a person. It is a signal lenders use to estimate lending risk. Payment history, balances, account age, credit mix, and inquiries can matter. But the goal is not to worship a number. The goal is to build a money life where borrowed money does not control you. The common mistake is treating rewards like they make all spending smart. Points and cash back can feel like winning, but they do not cancel out overspending or interest. A small reward does not beat a balance that follows you around for months. Think of credit like a sharp kitchen knife. It can be useful. It can also hurt you if you use it carelessly. The better question is not “Is credit good or bad?” The better question is, “Does this help me keep control, or does it make my cash flow easier to ignore?”',
	flashcards: [
		{
			id: 'debt-credit-interest--credit-is-a-tool-not-free-money--fc-main',
			role: 'main_idea',
			front: 'What is credit?',
			back: 'Credit is access to borrowed money that must be repaid under specific terms.',
		},
		{
			id: 'debt-credit-interest--credit-is-a-tool-not-free-money--fc-watch',
			role: 'watch_out',
			front: 'Why are rewards not enough?',
			back: 'Rewards do not cancel out overspending, interest, or balances that stick around.',
		},
		{
			id: 'debt-credit-interest--credit-is-a-tool-not-free-money--fc-use',
			role: 'use_it',
			front: 'What should credit support?',
			back: 'Credit should support your cash flow plan, not replace it.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Write your credit rule',
		prompt: 'Write one rule that would help you use credit as a tool instead of treating it like extra income.',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'debt-credit-interest--pick-a-payoff-lane',
	title: 'Pick a payoff lane',
	description:
		'You learn how debt payoff methods trade off motivation, interest cost, and behavior.',
	pillar: 'debt-credit-interest',
	schoolsOfThought: [
		'debt-free-ramsey',
		'credit-optimization',
		'behavioral-finance',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 2,
	tags: ['debt-payoff', 'snowball', 'avalanche', 'behavior'],
	prerequisites: ['debt-credit-interest--credit-is-a-tool-not-free-money'],
	relatedLessons: ['debt-credit-interest--debt-borrows-from-future-you'],
	concepts: [
		'debt-payoff-methods',
		'snowball-vs-avalanche',
		'behavior-vs-math',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Debt payoff gets easier when you pick a lane instead of randomly throwing money at whatever feels loudest. A payoff lane is a simple rule for choosing which debt gets extra money first. The two common frames are behavior-first and interest-first.',
		},
		{
			type: 'paragraph',
			text: 'Behavior-first means targeting small balances first so you create quick wins and momentum. Interest-first means targeting the highest interest rate first so you reduce total interest cost faster. Neither frame is magic. Each protects against a different failure.',
		},
		{
			type: 'bullet_list',
			items: [
				'Small-balance first protects motivation',
				'High-interest first protects math',
				'Minimums keep accounts current',
				'Extra payments create payoff progress',
			],
		},
		{
			type: 'callout',
			icon: '🧭',
			text: 'The best payoff lane is not the one that sounds smartest. It is the one you can actually follow.',
		},
		{
			type: 'paragraph',
			text: 'This matters because debt payoff is both math and behavior. If you only care about math but quit after three weeks, the math did not help. If you only care about motivation but ignore brutal interest, the debt may cost more than it needed to. A good plan respects both the spreadsheet and the human being using it.',
		},
		{
			type: 'example',
			label: 'Two lanes',
			text: 'Suppose you have a $300 store card, a $1,200 credit card, and a $4,500 personal loan. Small-balance first might attack the $300 first for a quick win. High-interest first might attack whichever debt has the highest rate. The right lesson is not that one is universally perfect. The lesson is that your extra dollars need a clear target.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is switching lanes every time your emotions change. One month you attack the scary balance. Next month you attack the annoying one. Then you stop because progress feels invisible. Random payoff creates random motivation. A lane gives the plan a rhythm.',
		},
		{
			type: 'paragraph',
			text: 'Think of debt payoff like clearing a traffic jam. Minimum payments keep every lane from fully blocking. Extra payments are the tow truck. If the tow truck keeps changing direction, nothing clears. If it focuses, one blockage disappears, then the next, then the next.',
		},
		{
			type: 'bullet_list',
			items: [
				'Keep minimums visible',
				'Choose the extra-payment target',
				'Track the balance shrinking',
				'Roll freed-up payment pressure forward',
			],
		},
		{
			type: 'paragraph',
			text: 'This lesson is education, not a command. Your real situation may involve hardship, negotiations, family obligations, or other constraints. But the principle still helps: make the debt visible, know the tradeoff, and give extra money a clear lane when extra money exists.',
		},
		{
			type: 'key_takeaway',
			text: 'Debt payoff needs a rule, because random payments create random progress.',
		},
	],
	audioScript:
		'Debt payoff needs a lane. Without one, you end up throwing money at whatever feels loudest that month. The scary balance. The annoying balance. The one with the app notification. The one your brain hates looking at. That can feel like action, but it often creates random progress. A payoff lane is just a rule for where extra debt money goes first. There are two common frames. One is behavior-first: attack small balances first so you get quick wins and momentum. The other is interest-first: attack the highest interest rate first so you reduce the total cost faster. Both protect against something real. Small-balance first protects motivation. High-interest first protects math. And debt payoff needs both. If the math is perfect but you quit after three weeks, the math did not save you. If motivation feels good but brutal interest keeps eating you alive, the plan may cost more than it needed to. Think about someone with a three hundred dollar store card, a twelve hundred dollar credit card, and a forty-five hundred dollar personal loan. A small-balance lane might wipe out the three hundred first. A high-interest lane might target the highest rate first. The point is not to declare one universal winner. The point is to stop drifting. Minimum payments keep accounts current. Extra payments create real movement. But extra payments need a target. Think of debt payoff like clearing a traffic jam. Minimums keep the lanes from fully blocking. Extra payments are the tow truck. If the tow truck keeps changing direction, nothing clears. If it focuses, one blockage disappears, then the next. That is the power of a lane.',
	flashcards: [
		{
			id: 'debt-credit-interest--pick-a-payoff-lane--fc-main',
			role: 'main_idea',
			front: 'What is a payoff lane?',
			back: 'A payoff lane is a rule for choosing which debt gets extra money first.',
		},
		{
			id: 'debt-credit-interest--pick-a-payoff-lane--fc-watch',
			role: 'watch_out',
			front: 'Why can random payoff fail?',
			back: 'Random payments can create random progress and make motivation harder to sustain.',
		},
		{
			id: 'debt-credit-interest--pick-a-payoff-lane--fc-use',
			role: 'use_it',
			front: 'What do payoff methods trade off?',
			back: 'They trade off motivation, interest cost, and what behavior you can actually repeat.',
		},
	],
	applyAction: {
		type: 'createBudgetPlan',
		label: 'Make room for extra payoff',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'debt-credit-interest',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--cq--q1',
			question: 'What is the best way to understand debt in this module?',
			options: [
				'A personal failure',
				'A claim on future cash flow',
				'Free money',
				'A number that only matters once a year',
			],
			correctIndex: 1,
			explanation:
				'Debt is framed as future cash flow already claimed, not a moral judgment.',
		},
		{
			type: 'true_false',
			id: 'debt-credit-interest--cq--q2',
			question:
				'Debt is only about the total balance, not monthly pressure.',
			correct: false,
			explanation:
				'The balance matters, but monthly payments affect your cash flow and options right now.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--cq--q3',
			question: 'What is interest?',
			options: [
				'A reward for borrowing',
				'The cost of borrowing money over time',
				'A fee that only applies to savings',
				'A credit score bonus',
			],
			correctIndex: 1,
			explanation:
				'Interest is the price of using borrowed money over time.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--cq--q4',
			question: 'Why can a low monthly payment be misleading?',
			options: [
				'It always means the debt is cheap',
				'It may hide a long repayment timeline and high total interest',
				'It means the balance is already gone',
				'It guarantees payoff in one year',
			],
			correctIndex: 1,
			explanation:
				'A low payment can feel manageable while the debt stays alive for a long time.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--cq--q5',
			scenario:
				'Jordan owes $3,000 and pays $90 every month, but the balance barely moves because interest takes a large part of the payment.',
			question: 'What is the best read?',
			options: [
				'Jordan is making payments, but not much progress',
				'Jordan has no debt pressure',
				'The interest does not matter',
				'The debt is already solved',
			],
			correctIndex: 0,
			explanation:
				'Payment and progress are not the same when interest eats part of the payment.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--cq--q6',
			question: 'What does a minimum payment usually do first?',
			options: [
				'Guarantees fast debt freedom',
				'Keeps the account current',
				'Removes all interest forever',
				'Builds an emergency fund',
			],
			correctIndex: 1,
			explanation:
				'Paying the minimum keeps the account current, but may not create strong payoff progress.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--cq--q7',
			scenario:
				'Taylor pays every minimum on time and feels responsible, but the balances stay around for months.',
			question: 'What is the module’s warning?',
			options: [
				'On-time minimums are always useless',
				'Minimums can keep you current without getting you free quickly',
				'Taylor should ignore the accounts',
				'Debt does not affect future options',
			],
			correctIndex: 1,
			explanation:
				'Minimums may prevent immediate damage but still stretch the payoff timeline.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q1',
			question: 'Which statement best captures Module 4?',
			options: [
				'Debt, credit, and interest are moral labels',
				'Debt, credit, and interest are forces that affect future cash flow',
				'Credit is always bad',
				'Interest never matters if the payment is small',
			],
			correctIndex: 1,
			explanation:
				'The module teaches how these forces change monthly pressure and future options.',
		},
		{
			type: 'true_false',
			id: 'debt-credit-interest--final--q2',
			question:
				'Having debt automatically means someone is bad with money.',
			correct: false,
			explanation:
				'The module avoids shame and treats debt as a financial force to understand.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--final--q3',
			scenario:
				'Sam owes $2,000, pays $75 minimums, and has only $100 of monthly gap.',
			question: 'What matters most for Sam’s snapshot?',
			options: [
				'Only the $2,000 balance',
				'The way the $75 payment consumes most of the monthly gap',
				'The name of the lender only',
				'Whether Sam feels embarrassed',
			],
			correctIndex: 1,
			explanation:
				'Monthly debt pressure matters because it reduces room for other goals.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q4',
			question: 'What is principal?',
			options: [
				'The amount borrowed',
				'The credit score',
				'The yearly rate',
				'The due date',
			],
			correctIndex: 0,
			explanation:
				'Principal is the borrowed amount before interest costs are added.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q5',
			question:
				'What is the main problem with focusing only on rewards points?',
			options: [
				'Rewards points always remove debt',
				'Rewards can distract from interest, overspending, and carried balances',
				'Rewards guarantee a better credit score',
				'Rewards replace cash flow planning',
			],
			correctIndex: 1,
			explanation:
				'Rewards do not cancel out the cost of interest or spending beyond the plan.',
		},
		{
			type: 'true_false',
			id: 'debt-credit-interest--final--q6',
			question:
				'Credit can be useful for one person and harmful for another depending on behavior and repayment.',
			correct: true,
			explanation:
				'Credit is a tool. Its effect depends on cash flow, repayment, interest, and habits.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--final--q7',
			scenario:
				'Person A uses a card for planned groceries and pays it off before interest becomes a problem. Person B uses the card for unplanned spending and carries the balance.',
			question: 'What is the best lesson?',
			options: [
				'The tool is identical, but the behavior changes the outcome',
				'Both people have the same debt risk',
				'Credit is always free money',
				'Credit scores are the only thing that matters',
			],
			correctIndex: 0,
			explanation:
				'Credit supports or attacks cash flow depending on behavior.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q8',
			question: 'What does small-balance-first payoff usually protect?',
			options: [
				'Motivation and quick wins',
				'The lowest interest cost in every situation',
				'A higher rent payment',
				'Ignoring all balances',
			],
			correctIndex: 0,
			explanation:
				'Small-balance-first is behavior-focused and may help momentum.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q9',
			question: 'What does high-interest-first payoff usually protect?',
			options: [
				'The emotional win of clearing the smallest balance',
				'Reducing interest cost faster',
				'Avoiding all payments',
				'Increasing fixed costs',
			],
			correctIndex: 1,
			explanation:
				'High-interest-first focuses on reducing the cost of debt faster.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--final--q10',
			scenario:
				'Nina switches her payoff target every month based on which debt feels most annoying.',
			question: 'What is the likely problem?',
			options: [
				'She has a clear lane',
				'Her payoff progress may feel random and hard to sustain',
				'She is guaranteed to minimize interest',
				'She no longer needs a budget',
			],
			correctIndex: 1,
			explanation:
				'Switching targets constantly can make the payoff plan feel scattered.',
		},
		{
			type: 'multiple_choice',
			id: 'debt-credit-interest--final--q11',
			question:
				'What is the best question to ask about a new credit purchase?',
			options: [
				'Will this give me points?',
				'Does this fit my cash flow and repayment plan?',
				'Can I ignore the due date?',
				'Will the minimum payment make it disappear quickly?',
			],
			correctIndex: 1,
			explanation:
				'Credit should support the cash flow plan, not replace it.',
		},
		{
			type: 'scenario',
			id: 'debt-credit-interest--final--q12',
			scenario:
				'Chris has three debts and finally creates a rule for where extra payments go after minimums are covered.',
			question: 'What principle is Chris using?',
			options: [
				'Pick a payoff lane',
				'Treat credit as income',
				'Ignore interest',
				'Let the lender set every goal',
			],
			correctIndex: 0,
			explanation:
				'A payoff lane gives extra payments a clear target instead of creating random progress.',
		},
	],
};

export const DEBT_CREDIT_INTEREST_MODULE: LearningModule = {
	id: 'debt-credit-interest',
	title: 'Debt, Credit & Interest',
	description:
		'See debt as future cash flow already claimed — not a personal failure. Learn how interest, minimum payments, and credit behavior shape your options, then pick a payoff lane you can actually follow.',
	emoji: '🧱',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'debt-credit-interest',
	order: 4,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
